import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { 
  USERS, OFFICES, COMPANIES, MODULES,
  authenticateUser, getUserById, getOfficeById, getOfficeByOwnerId,
  getCompanyById, getOfficesByCompanyId,
  getBookingsByUserId, getOrdersByUserId, getBookingsByOfficeId, getOrdersByOfficeId,
  getServicesByOfficeId, getProductsByOfficeId, getResourcesByOfficeId,
  enrichBooking, enrichOrder,
} from '../data/database';

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

// Logo URLs
export const LOGOS = {
  dark: 'https://customer-assets.emergentagent.com/job_632f9d2e-3dbf-44a0-847b-71ec27868595/artifacts/qndi2j7i_logo.a291411cbf7fc3847246.png',
  light: 'https://customer-assets.emergentagent.com/job_632f9d2e-3dbf-44a0-847b-71ec27868595/artifacts/qndi2j7i_logo.a291411cbf7fc3847246.png',
  full: 'https://customer-assets.emergentagent.com/job_632f9d2e-3dbf-44a0-847b-71ec27868595/artifacts/qndi2j7i_logo.a291411cbf7fc3847246.png'
};

// Default user (not logged in)
const DEFAULT_USER = {
  id: null,
  firstName: 'Ospite',
  lastName: '',
  email: '',
  phone: '',
  avatar: null,
  role: 'guest',
  // Programs
  starProgram: { level: 0, referralCode: '', referrals: 0, credits: 0 },
  elitProgram: { level: 0, totalPurchases: 0 },
  favorites: { offices: [], services: [], products: [], events: [], courses: [] },
  notifications: 0,
  // Partner specific
  officeId: null,
  plan: null,
  isElitPartner: false,
};

export const AppProvider = ({ children }) => {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [pendingAction, setPendingAction] = useState(null);
  const [loginError, setLoginError] = useState('');

  // User state
  const [currentUser, setCurrentUser] = useState(DEFAULT_USER);
  const [userMode, setUserMode] = useState('consumer'); // 'consumer' | 'partner'
  
  // Current company & selected office (for partner mode)
  const [currentCompany, setCurrentCompany] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  
  // Cart
  const [cart, setCart] = useState([]);
  
  // Favorites (synced with user)
  const [favorites, setFavorites] = useState({
    offices: [],
    services: [],
    products: [],
    events: [],
    courses: []
  });
  
  // AI Chat
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    { role: 'assistant', content: 'Ciao! Sono il tuo assistente Skipres. Come posso aiutarti oggi?' }
  ]);
  
  // Calendar Items (personal tasks)
  const [calendarItems, setCalendarItems] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return [
      { id: 1, title: 'Riunione di lavoro', description: 'Discussione progetto Q1', date: today, time: '10:00', duration: 60, location: 'Ufficio', priority: 'high', icon: 'briefcase' },
      { id: 2, title: 'Palestra', description: 'Allenamento cardio', date: today, time: '18:00', duration: 90, location: 'Fit Zone Gym', priority: 'medium', icon: 'gym' },
    ];
  });

  // ============================================
  // AUTH FUNCTIONS
  // ============================================
  
  const login = (email, password) => {
    setLoginError('');
    const user = authenticateUser(email, password);
    
    if (user) {
      setCurrentUser(user);
      setIsLoggedIn(true);
      setFavorites(user.favorites || { offices: [], services: [], products: [], events: [], courses: [] });
      
      // If partner, load company and first office
      if (user.role === 'partner' && user.companyId) {
        const company = getCompanyById(user.companyId);
        setCurrentCompany(company);
        const offices = getOfficesByCompanyId(user.companyId);
        if (offices.length > 0) {
          setSelectedOffice(offices[0]);
        }
        setUserMode('partner');
      } else {
        setCurrentCompany(null);
        setSelectedOffice(null);
        setUserMode('consumer');
      }
      
      setShowAuthModal(false);
      
      // Execute pending action if any
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
      
      return true;
    } else {
      setLoginError('Email o password non corretti');
      return false;
    }
  };
  
  const logout = () => {
    setCurrentUser(DEFAULT_USER);
    setCurrentCompany(null);
    setSelectedOffice(null);
    setIsLoggedIn(false);
    setUserMode('consumer');
    setFavorites({ offices: [], services: [], products: [], events: [], courses: [] });
    setCart([]);
  };
  
  const toggleUserMode = () => {
    if (currentUser.role === 'partner') {
      setUserMode(prev => prev === 'consumer' ? 'partner' : 'consumer');
    }
  };
  
  // Become partner (for consumers)
  const becomePartner = (planType = 'start') => {
    setCurrentUser(prev => ({
      ...prev,
      role: 'partner',
      plan: planType,
      companyId: 1, // Would be assigned by backend
    }));
    
    // Load demo company and office
    const company = getCompanyById(1);
    setCurrentCompany(company);
    const offices = getOfficesByCompanyId(1);
    if (offices.length > 0) setSelectedOffice(offices[0]);
    setUserMode('partner');
  };

  // ============================================
  // REQUIRE AUTH
  // ============================================
  
  const requireAuth = (action) => {
    if (isLoggedIn) {
      action();
    } else {
      setPendingAction(() => action);
      setAuthMode('login');
      setShowAuthModal(true);
    }
  };

  // ============================================
  // USER DATA GETTERS (from database)
  // ============================================
  
  // Get user's bookings
  const userBookings = useMemo(() => {
    if (!currentUser.id) return [];
    return getBookingsByUserId(currentUser.id).map(enrichBooking);
  }, [currentUser.id]);
  
  // Get user's orders
  const userOrders = useMemo(() => {
    if (!currentUser.id) return [];
    return getOrdersByUserId(currentUser.id).map(enrichOrder);
  }, [currentUser.id]);
  
  // Get partner's bookings
  const partnerBookings = useMemo(() => {
    if (!selectedOffice?.id) return [];
    return getBookingsByOfficeId(selectedOffice.id).map(enrichBooking);
  }, [selectedOffice?.id]);
  
  // Get partner's orders
  const partnerOrders = useMemo(() => {
    if (!selectedOffice?.id) return [];
    return getOrdersByOfficeId(selectedOffice.id).map(enrichOrder);
  }, [selectedOffice?.id]);
  
  // Get partner's services
  const partnerServices = useMemo(() => {
    if (!selectedOffice?.id) return [];
    return getServicesByOfficeId(selectedOffice.id);
  }, [selectedOffice?.id]);
  
  // Get partner's products
  const partnerProducts = useMemo(() => {
    if (!selectedOffice?.id) return [];
    return getProductsByOfficeId(selectedOffice.id);
  }, [selectedOffice?.id]);
  
  // Get partner's resources
  const partnerResources = useMemo(() => {
    if (!selectedOffice?.id) return [];
    return getResourcesByOfficeId(selectedOffice.id);
  }, [selectedOffice?.id]);
  
  // Get office module config
  const officeModuleConfig = useMemo(() => {
    if (!selectedOffice?.enabledModules) return null;
    const primaryModule = selectedOffice.enabledModules[0];
    return MODULES[primaryModule] || null;
  }, [selectedOffice?.enabledModules]);
  
  // Get all offices for current company (for office switcher)
  const companyOffices = useMemo(() => {
    if (!currentCompany?.id) return [];
    return getOfficesByCompanyId(currentCompany.id);
  }, [currentCompany?.id]);

  // Switch to a different office
  const switchOffice = (officeId) => {
    const office = getOfficeById(officeId);
    if (office && office.companyId === currentCompany?.id) {
      setSelectedOffice(office);
    }
  };

  // ============================================
  // AI CHAT
  // ============================================
  
  useEffect(() => {
    const userName = currentUser.firstName || 'Ospite';
    if (userMode === 'partner' && selectedOffice) {
      setAiMessages([
        { role: 'assistant', content: `Ciao ${userName}! Sono il tuo Business Assistant per ${selectedOffice.name}. Posso aiutarti ad analizzare le vendite, scoprire i servizi più richiesti, suggerirti strategie per aumentare le prenotazioni. Cosa vuoi sapere?` }
      ]);
    } else {
      setAiMessages([
        { role: 'assistant', content: `Ciao ${userName}! Sono il tuo assistente Skipres. Come posso aiutarti oggi?` }
      ]);
    }
  }, [userMode, selectedOffice, currentUser.firstName]);

  // ============================================
  // LEGACY COMPUTED VALUES (for compatibility)
  // ============================================
  
  // Build user object compatible with old code
  const user = useMemo(() => ({
    id: currentUser.id,
    role: currentUser.role || 'consumer',
    name: `${currentUser.firstName} ${currentUser.lastName}`.trim() || 'Ospite',
    firstName: currentUser.firstName,
    lastName: currentUser.lastName,
    avatar: currentUser.avatar,
    email: currentUser.email,
    phone: currentUser.phone,
    // Programs
    isStarMember: currentUser.starProgram?.level > 0,
    starLevel: currentUser.starProgram?.level || 0,
    starEarnings: currentUser.starProgram?.credits || 0,
    starReferrals: currentUser.starProgram?.referrals || 0,
    elitLevel: currentUser.elitProgram?.level || 0,
    elitPurchases: currentUser.elitProgram?.totalPurchases || 0,
    // Partner
    isPartner: currentUser.role === 'partner',
    companyId: currentUser.companyId,
    businessId: currentUser.companyId, // Legacy alias
    businessName: currentCompany?.name || '',
    companyName: currentCompany?.name || '',
    officeName: selectedOffice?.name || '',
    businessPlan: currentUser.plan,
    isElitPartner: currentUser.isElitPartner || currentCompany?.isElit,
    // Other
    notifications: currentUser.notifications || 0,
    hasCalendarPro: false,
  }), [currentUser, currentCompany, selectedOffice]);

  // ============================================
  // CART FUNCTIONS
  // ============================================
  
  const addToCart = (item, type = 'product') => {
    const existing = cart.find(c => c.id === item.id && c.type === type);
    if (existing) {
      setCart(cart.map(c => c.id === item.id && c.type === type ? { ...c, qty: c.qty + 1 } : c));
    } else {
      setCart([...cart, { ...item, type, qty: 1 }]);
    }
  };

  const removeFromCart = (itemId, type) => {
    setCart(cart.filter(c => !(c.id === itemId && c.type === type)));
  };

  const updateCartQty = (itemId, type, qty) => {
    if (qty <= 0) {
      removeFromCart(itemId, type);
    } else {
      setCart(cart.map(c => c.id === itemId && c.type === type ? { ...c, qty } : c));
    }
  };

  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((acc, item) => acc + (item.price * item.qty), 0);
  const cartCount = cart.reduce((acc, item) => acc + item.qty, 0);

  // ============================================
  // FAVORITES FUNCTIONS
  // ============================================

  const toggleFavorite = (itemId, type = 'offices') => {
    setFavorites(prev => {
      const currentList = prev[type] || [];
      return {
        ...prev,
        [type]: currentList.includes(itemId)
          ? currentList.filter(id => id !== itemId)
          : [...currentList, itemId]
      };
    });
  };

  const isFavorite = (itemId, type = 'offices') => {
    return favorites[type]?.includes(itemId) || false;
  };

  // ============================================
  // LEGACY COMPATIBILITY FUNCTIONS
  // ============================================

  // Activate services (legacy - kept for compatibility)
  const activateCalendarPro = () => {};
  const activateStarProgram = () => {
    setCurrentUser(prev => ({
      ...prev,
      starProgram: { ...prev.starProgram, level: 1 }
    }));
  };
  const activateElitPartner = () => {
    setCurrentUser(prev => ({ ...prev, isElitPartner: true }));
  };
  
  // Legacy setUser (for compatibility with old code)
  const setUser = (updater) => {
    if (typeof updater === 'function') {
      setCurrentUser(prev => {
        const updated = updater(prev);
        return { ...prev, ...updated };
      });
    } else {
      setCurrentUser(prev => ({ ...prev, ...updater }));
    }
  };
  
  // Today's agenda badge count
  const todayAgendaCount = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const timeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    };

    // Count today's bookings
    const todayBookings = userBookings.filter(b => {
      if (b.date !== todayStr) return false;
      if (b.status === 'completed' || b.status === 'cancelled') return false;
      const bookingMinutes = timeToMinutes(b.time);
      return bookingMinutes > currentMinutes;
    });

    // Count today's personal events
    const todayEvents = calendarItems.filter(item => {
      if (item.date !== todayStr) return false;
      const itemMinutes = timeToMinutes(item.time);
      return itemMinutes > currentMinutes;
    });

    return todayBookings.length + todayEvents.length;
  }, [userBookings, calendarItems]);

  // ============================================
  // CONTEXT PROVIDER
  // ============================================

  return (
    <AppContext.Provider value={{
      // Auth
      isLoggedIn,
      showAuthModal,
      setShowAuthModal,
      authMode,
      setAuthMode,
      login,
      logout,
      requireAuth,
      loginError,
      
      // User
      userMode,
      setUserMode,
      toggleUserMode,
      user,
      setUser,
      currentUser,
      setCurrentUser,
      
      // Company & Office (partner)
      currentCompany,
      setCurrentCompany,
      selectedOffice,
      setSelectedOffice,
      switchOffice,
      companyOffices,
      // Legacy alias
      selectedOffice: selectedOffice,
      setCurrentBusiness: setSelectedOffice,
      officeModuleConfig,
      officeTypeConfig: officeModuleConfig, // Legacy alias
      businessTypeConfig: officeModuleConfig, // Legacy alias
      
      // Data from database
      userBookings,
      userOrders,
      partnerBookings,
      partnerOrders,
      partnerServices,
      partnerProducts,
      partnerResources,
      
      // Services activation (legacy)
      activateCalendarPro,
      activateStarProgram,
      activateElitPartner,
      becomePartner,
      
      // Cart
      cart,
      addToCart,
      removeFromCart,
      updateCartQty,
      clearCart,
      cartTotal,
      cartCount,
      
      // Today's agenda count
      todayAgendaCount,
      
      // Calendar items (personal tasks)
      calendarItems,
      setCalendarItems,
      
      // Favorites
      favorites,
      toggleFavorite,
      isFavorite,
      
      // AI Chat
      aiChatOpen,
      setAiChatOpen,
      aiMessages,
      setAiMessages,
      
      // Assets
      LOGOS
    }}>
      {children}
    </AppContext.Provider>
  );
};
